import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App.tsx'
import { setAccessToken, setApiBaseUrl } from './api.ts'
import { installBridge, reportReady } from './bridge.ts'
import { applyTheme, routeScreen } from './contracts.ts'
import { captureAuthCallbackQuery } from './oauth.ts'
import { navigate } from './router.ts'
import './styles.css'

applyTheme()
// Before the bridge reports a screen, before the first fetch, before React —
// the authorization code and `state` do not get to be in the URL that long.
captureAuthCallbackQuery()

function contractPath(value: string): string | null {
  try {
    const url = new URL(value, window.location.origin)
    if (url.origin !== window.location.origin || routeScreen(url.pathname).id === 'notFound') return null
    return `${url.pathname}${url.search}${url.hash}`
  } catch { return null }
}

function nativeSessionChanged(authenticated: boolean): void {
  window.dispatchEvent(new CustomEvent('bellhook:native-session-changed', { detail: { authenticated } }))
}

installBridge((message) => {
  switch (message.type) {
    case 'configure':
      setApiBaseUrl(message.payload.apiBaseUrl)
      setAccessToken(message.payload.accessToken ?? null)
      if (message.payload.colorScheme) applyTheme(message.payload.colorScheme)
      if (message.payload.accessToken !== undefined) nativeSessionChanged(message.payload.accessToken !== null)
      if (message.payload.initialPath) {
        const path = contractPath(message.payload.initialPath)
        if (path && window.location.pathname !== new URL(path, window.location.origin).pathname) navigate(path, true)
      }
      break
    case 'sessionChanged':
      setAccessToken(message.payload.accessToken)
      nativeSessionChanged(message.payload.accessToken !== null)
      window.dispatchEvent(new Event('bellhook:refresh'))
      break
    case 'navigate':
      {
        const path = contractPath(message.payload.path)
        if (path) navigate(path)
      }
      break
    case 'refresh':
      window.dispatchEvent(new Event('bellhook:refresh'))
      break
  }
})
reportReady(routeScreen(window.location.pathname).id)
void fetch('/__tk/report', {
  method: 'POST', headers: { 'content-type': 'application/json' },
  body: JSON.stringify({ type: 'start' }),
}).catch(() => undefined)

const root = document.getElementById('root')
if (!root) throw new Error('root element is missing')
// The join route contains a safe server-rendered preview for first paint.
// App owns the tree once JavaScript starts, so remove that fallback first.
root.replaceChildren()
createRoot(root).render(<StrictMode><App /></StrictMode>)
